import React, { useRef, useState, useEffect } from "react";
import {
    Box,
    Paper,
    Skeleton,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Rating,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    TextField
} from "@mui/material";

import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';

import FormDocument from "./FormDocument";
import ResultDocumentCard from "./ResultDocumentCard";
import UpgradePlanDialog from "../Billing/UpgradePlanDialog";

import api from '../../services/api';
import { getApiErrorMessage, normalizePlan } from '../../services/apiUtils';

import { useSnackbar } from "../../contexts/SnackbarContext";
import { useAuth } from "../../contexts/AuthContext";
import { usePlano } from "../../contexts/PlanContext";
import { usePlanModal } from "../../contexts/PlanModalContext";

const MOTIVOS_POSITIVOS = [
    "O resultado do documento me ajudou",
    "Foi fácil usar a plataforma",
    "A geração foi rápida",
    "Conteúdo bem alinhado à BNCC",
    "Gostei do visual da plataforma"
];

const MOTIVOS_NEGATIVOS = [
    "O resultado não ficou como eu esperava",
    "Tive dificuldade para usar a plataforma",
    "A geração demorou",
    "Conteúdo desalinhado com à BNCC",
    "Não consegui adicionar imagens nas questões com ilustração"
];

const getLoadingSnackInitialSteps = (isPlanoAula = false) => [
    { delay: 0, message: "Preparando seu material… ⌛" },
    { delay: 6000, message: "Alinhando o material ao tema…" },
    { delay: 12000, message: "Alinhando o material à BNCC e ao nível selecionado…" },
    {
        delay: 18000,
        message: isPlanoAula
            ? "Estruturando o conteúdo e organizando os tópicos…"
            : "Estruturando o conteúdo e organizando as questões…"
    }
];

const LOADING_SNACK_ROTATING_MESSAGES = [
    "Estamos lapidando seu material para entregar um resultado mais consistente…",
    "Conferindo a organização do conteúdo e a clareza pedagógica…",
    "Ajustando os últimos detalhes para o documento ficar bem estruturado…",
    "Seu material está quase pronto…",
    "Dando os toques finais no seu material… ✨"
];

function isDifferentMonth(isoStringDate) {
    if (!isoStringDate) return false;

    const adesao = new Date(isoStringDate);
    const hoje = new Date();

    const anoMesAdesao = `${adesao.getFullYear()}-${adesao.getMonth() + 1}`;
    const anoMesAtual = `${hoje.getFullYear()}-${hoje.getMonth() + 1}`;

    return anoMesAdesao !== anoMesAtual;
}

function isPlanoAulaPayload(dadosFormulario) {
    const candidates = [
        dadosFormulario?.tipo_conteudo,
        dadosFormulario?.tipoConteudo,
        dadosFormulario?.tipo,
        dadosFormulario?.tipoDocumento,
        dadosFormulario?.documento,
    ];

    return candidates.some((value) =>
        String(value || "")
            .normalize("NFD")
            .replace(/[̀-ͯ]/g, "")
            .toLowerCase()
            .includes("plano")
    );
}


export default function DocumentResultView() {
    const { user } = useAuth();
    const { planoStatus, restantes, incrementaTrial, incrementaAssinante, fetchStatusPlano, getQuotaAtual, planoTipo } = usePlano();
    const { openPlanModal } = usePlanModal();

    const [resultado, setResultado] = useState();
    const [loading, setLoading] = useState(false);
    const { showSnackbar, closeSnackbar } = useSnackbar();
    const [openUpgrade, setOpenUpgrade] = useState(false);

    const isMObile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const formRef = useRef();
    const resultRef = useRef();

    const gerarLockRef = useRef(false); // trava anti-duplo clique (instantânea)

    const loadingSnackTimersRef = useRef([]);
    const loadingSnackIntervalRef = useRef(null);

    const clearLoadingSnackTimers = () => {
        loadingSnackTimersRef.current.forEach(clearTimeout);
        loadingSnackTimersRef.current = [];

        if (loadingSnackIntervalRef.current) {
            clearInterval(loadingSnackIntervalRef.current);
            loadingSnackIntervalRef.current = null;
        }
    };

    const startLoadingSnackSequence = (dadosFormulario) => {
        clearLoadingSnackTimers();

        const isPlanoAula = isPlanoAulaPayload(dadosFormulario);

        getLoadingSnackInitialSteps(isPlanoAula).forEach(({ delay, message }) => {
            const timer = setTimeout(() => {
                showSnackbar(message, "info");
            }, delay);

            loadingSnackTimersRef.current.push(timer);
        });

        const rotatingStartDelay = 30000; // começa a rotacionar depois das mensagens iniciais
        const rotatingEvery = 10000;      // troca a cada 10s
        let rotatingIndex = 0;

        const startRotationTimer = setTimeout(() => {
            showSnackbar(LOADING_SNACK_ROTATING_MESSAGES[rotatingIndex], "info");
            rotatingIndex = (rotatingIndex + 1) % LOADING_SNACK_ROTATING_MESSAGES.length;

            loadingSnackIntervalRef.current = setInterval(() => {
                showSnackbar(LOADING_SNACK_ROTATING_MESSAGES[rotatingIndex], "info");
                rotatingIndex = (rotatingIndex + 1) % LOADING_SNACK_ROTATING_MESSAGES.length;
            }, rotatingEvery);
        }, rotatingStartDelay);

        loadingSnackTimersRef.current.push(startRotationTimer);
    };

    // Avaliação da primeira geração do período gratuito
    const [openAvaliacao, setOpenAvaliacao] = useState(false);
    const [avaliacaoJaMostrada, setAvaliacaoJaMostrada] = useState(false);
    const [avaliacaoEstrelas, setAvaliacaoEstrelas] = useState(0);
    const [avaliacaoMotivos, setAvaliacaoMotivos] = useState([]);
    const [avaliacaoDescricao, setAvaliacaoDescricao] = useState("");
    const [avaliacaoLoading, setAvaliacaoLoading] = useState(false);
    const [avaliacaoContexto, setAvaliacaoContexto] = useState(null);
    const [mostrarChamadaAvaliacao, setMostrarChamadaAvaliacao] = useState(false);
    const [tipoChamadaAvaliacao, setTipoChamadaAvaliacao] = useState("primeira_avaliacao_trial");

    // Rola para o resultado sempre que loading começa
    useEffect(() => {
        if (loading && resultRef.current) {
            resultRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [loading]);

    useEffect(() => {
        return () => {
            clearLoadingSnackTimers();
        };
    }, []);

    const handleGerarDocumento = async (dadosFormulario) => {

        if (gerarLockRef.current) return; // evita 2 cliques no mesmo tick
        gerarLockRef.current = true;

        setLoading(true); // desabilita UI imediatamente
        if (resultRef.current) {
            resultRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); // scroll imediato
        }


        setResultado(null);
        setMostrarChamadaAvaliacao(false);
        setAvaliacaoJaMostrada(false);
        setTipoChamadaAvaliacao(null);
        setAvaliacaoContexto(null);

        // Descobre se deve exibir chamada de avaliação (trial ou assinante)
        // Faz isso antes da validação de plano, sem alterar o fluxo atual.
        let deveMostrarAvaliacao = false;

        try {
            const respPlano = await api.get('/billing/plan');
            const planoDados = normalizePlan(respPlano?.data.plan);

            const usadas = Number(planoDados?.usedGeneration ?? 0);

            // TRIAL: apenas 1ª geração
            if (planoDados && planoDados.status === 'G' && usadas === 0) {
                deveMostrarAvaliacao = true;
                setTipoChamadaAvaliacao("primeira_avaliacao_trial");
                setAvaliacaoContexto("primeira_avaliacao_trial");
            }

            // ASSINANTE/PARCEIRO: 1ª geração do mês (usedGeneration = 0 e status = 'A' ou 'B')
            if (
                planoDados &&
                ["A", "B", "I"].includes(planoDados.status) &&
                usadas === 0
            ) {
                deveMostrarAvaliacao = true;
                setTipoChamadaAvaliacao(isDifferentMonth(planoDados.subscribedAt) ? "avaliacao_mensal_assinante" : "avaliacao_assinante");
                setAvaliacaoContexto(isDifferentMonth(planoDados.subscribedAt) ? "avaliacao_mensal_assinante" : "avaliacao_assinante");
            }

        } catch (e) {
            console.warn('Não foi possível verificar se deve mostrar a avaliação.', e);
        }

        try {
            // 1) Valida permissão/limite antes de chamar a IA
            const respPermissao = await api.get('/billing/plan/validate');
            if (!respPermissao.data?.status) {

                clearLoadingSnackTimers();

                const usuarioParceiro = planoStatus === "B" || respPermissao.data?.type === "parceiro";

                if (planoStatus === "G") {

                    showSnackbar(respPermissao.data?.message || respPermissao.data?.error || "Permissão negada. Faça sua assinatura.", "warning");
                    openPlanModal();
                } else if (usuarioParceiro) {
                    showSnackbar(respPermissao.data?.message || respPermissao.data?.error || "Limite do acesso parceiro atingido.", "warning");
                } else {
                    showSnackbar(respPermissao.data?.message || respPermissao.data?.error || "Permissão negada. Faça sua assinatura.", "warning",
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                                closeSnackbar();        // fecha o snackbar
                                planoStatus === "A" ? setOpenUpgrade(true) : openPlanModal();   // ação principal
                            }}
                            sx={{
                                borderRadius: 5,
                                bgcolor: '#fff',
                                color: '#000',
                                fontWeight: "bold",
                                fontSize: 11,
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                },
                            }}
                        >
                            <RocketLaunchRoundedIcon sx={{ fontSize: 12, fontWeight: "bold", mr: 0.2 }} />{planoStatus === "A" ? "Atualizar Plano" : "Ativar Plano"}
                        </Button>
                    );
                }


                // Atualiza UI quando for bloqueio por limite
                if (["trial", "assinante", "parceiro", "plano_inativo", "plano_invalido"].includes(respPermissao.data?.type)) {
                    fetchStatusPlano(user?.id);
                }
                return; // early-exit
            }

            // Preferir o tipo mais recente da validação (evita depender de planoStatus potencialmente “stale”)
            const tipoPlanoAtual = respPermissao.data?.type; // 'trial' | 'assinante' | 'parceiro'

            //setLoading(true);
            startLoadingSnackSequence(dadosFormulario);

            // 2) Gera documento (IA)
            const resp = await api.post('/pedagogical-content/generate', dadosFormulario, { timeout: 240_000 });

            if (!resp.data || Object.keys(resp.data).length === 0) {
                clearLoadingSnackTimers();
                showSnackbar("Não foi possível gerar o documento para esses dados.", "warning");
                setResultado(null);
                return;
            }

            //console.log(resp.data);
            setResultado(resp.data);

            // Se for um dos contextos de avaliação configurados acima, mostra a chamada
            if (deveMostrarAvaliacao && !avaliacaoJaMostrada) {
                setAvaliacaoEstrelas(0);
                setAvaliacaoMotivos([]);
                setAvaliacaoDescricao("");
                setMostrarChamadaAvaliacao(true);
            }

            // 3) Incrementa consumo (apenas após sucesso)
            if (tipoPlanoAtual === "trial") {
                // Se ainda quer usar o guard local:
                if (planoStatus === "G" && restantes > 0) {
                    await incrementaTrial(user?.id);
                }
            } else if (["assinante", "parceiro"].includes(tipoPlanoAtual)) {
                const inc = await incrementaAssinante(user?.id);
                if (!inc?.status) {
                    if (tipoPlanoAtual === "parceiro") {
                        showSnackbar(inc?.message || inc?.error || "Limite mensal do acesso parceiro atingido.", "warning");
                    } else {
                        showSnackbar(inc?.message || inc?.error || "Limite mensal atingido.", "warning",
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                                closeSnackbar();        // 👈 fecha o snackbar
                                setOpenUpgrade(true);   // 👈 ação principal
                            }}
                            sx={{
                                borderRadius: 5,
                                bgcolor: '#fff',
                                color: '#000',
                                fontWeight: "bold",
                                fontSize: 11,
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                },
                            }}
                        >
                            <RocketLaunchRoundedIcon sx={{ fontSize: 12, fontWeight: "bold", mr: 0.2 }} />Atualizar Plano
                        </Button>
                    );
                    }
                } else {
                    // Usa o 'restantes' devolvido pelo backend; fallback em (restantes - 1)
                    const restantesAtual = typeof inc?.restantes === "number" ? inc.restantes : (restantes - 1);

                    // Aviso dinâmico: quando restar apenas 30% da quota (70% já usado)
                    const quota = getQuotaAtual() || 0; // 10 | 25 | 50
                    const limiarAviso = Math.ceil(quota * 0.3); // Faltam respectivamente: 3 | 8 | 15

                    if (quota > 0 && restantesAtual === limiarAviso) {
                        if (tipoPlanoAtual === "parceiro") {
                            showSnackbar(`Atenção: restam apenas ${limiarAviso} gerações do acesso parceiro neste mês.`, "warning");
                        } else {
                            showSnackbar(`Atenção: restam apenas ${limiarAviso} gerações neste mês.`, "warning",
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                    closeSnackbar();        // 👈 fecha o snackbar
                                    setOpenUpgrade(true);   // 👈 ação principal
                                }}
                                sx={{
                                    borderRadius: 5,
                                    bgcolor: '#fff',
                                    color: '#000',
                                    fontWeight: "bold",
                                    fontSize: 11,
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor: '#f5f5f5',
                                    },
                                }}
                            >
                                <RocketLaunchRoundedIcon sx={{ fontSize: 12, fontWeight: "bold", mr: 0.2 }} />Atualizar Plano
                            </Button>);
                        }
                    }

                }
                // Sincroniza com o servidor
                fetchStatusPlano(user?.id);
            }


        } catch (err) {
            clearLoadingSnackTimers();

            const isTimeout = err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message || ""));
            showSnackbar(
                isTimeout
                    ? "A geração demorou mais que o esperado. Tente novamente em instantes ou verifique os logs da geração no backend."
                    : getApiErrorMessage(err, "Erro ao gerar documento. Tente novamente."),
                "error"
            );
            setResultado(null);
        } finally {
            clearLoadingSnackTimers();
            setLoading(false);
            gerarLockRef.current = false; // libera clique quando terminar (sucesso/erro/bloqueio)
        }
    };

    const handleFecharAvaliacao = () => {
        if (avaliacaoLoading) return;
        setOpenAvaliacao(false);
        setAvaliacaoJaMostrada(true);
    };

    const handleChangeEstrelas = (_, newValue) => {
        const valor = newValue || 0;
        setAvaliacaoEstrelas(valor);
        // sempre que trocar as estrelas, limpamos os motivos selecionados
        setAvaliacaoMotivos([]);
    };

    const handleToggleMotivo = (motivo) => {
        setAvaliacaoMotivos((prev) =>
            prev.includes(motivo)
                ? prev.filter((m) => m !== motivo)
                : [...prev, motivo]
        );
    };

    const handleEnviarAvaliacao = async () => {
        if (!avaliacaoEstrelas) {
            showSnackbar("Escolha uma quantidade de estrelas antes de enviar.", "warning");
            return;
        }

        setAvaliacaoLoading(true);

        try {
            await api.post('/relationship/evaluations', {
                stars: avaliacaoEstrelas,
                reasons: avaliacaoMotivos,
                description: avaliacaoDescricao,
                context: avaliacaoContexto
            });

            showSnackbar("Obrigado pelo seu feedback! ✨", "success");
            setOpenAvaliacao(false);
            setAvaliacaoJaMostrada(true);
            setMostrarChamadaAvaliacao(false);

            setTimeout(() => {
                setAvaliacaoEstrelas(0);
                setAvaliacaoMotivos([]);
                setAvaliacaoDescricao("");
                setAvaliacaoContexto(null);
            }, 500);

        } catch (err) {
            console.error('Erro ao enviar avaliação:', err);
            showSnackbar(getApiErrorMessage(err, "Não foi possível salvar sua avaliação. Tente novamente mais tarde."), "error");
        } finally {
            setAvaliacaoLoading(false);
        }
    };

    const handleNovaConsulta = () => {
        setResultado(null);
        setLoading(false);
        if (formRef.current && typeof formRef.current.reset === "function") {
            formRef.current.reset();
        }
    };

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    mx: "auto",
                    mt: { xs: 3, md: 4 }
                }}
            >
                {/* Formulário: menor, centralizado */}
                <Box sx={{
                    width: isMObile ? "100%" : "80%",
                    mx: "auto",
                    mb: 4
                }}>
                    <FormDocument ref={formRef} onGerarDocumento={handleGerarDocumento} disabled={loading} />
                </Box>

                {/* Resultado: maior, centralizado */}
                <Box sx={{
                    width: isMObile ? "100%" : "90%",
                    mx: "auto"
                }} ref={resultRef}>
                    {loading ? (
                        <Paper sx={{ p: 3, borderRadius: 4, width: "100%" }}>
                            <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
                            <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
                            <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
                            <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
                            <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
                            <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
                            <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
                            <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
                            <Skeleton variant="rounded" height={36} width="60%" sx={{ mb: 1, ml: 'auto' }} />
                        </Paper>
                    ) : (
                        <ResultDocumentCard
                            resultado={resultado}
                            onNovaConsulta={handleNovaConsulta}
                            mostrarAvaliacao={mostrarChamadaAvaliacao}
                            onAvaliarClick={() => setOpenAvaliacao(true)}
                            tipoChamadaAvaliacao={tipoChamadaAvaliacao}
                        />

                    )}
                </Box>
            </Box>

            {/* Dialog de avaliação da primeira geração do período gratuito */}
            <Dialog
                open={openAvaliacao}
                onClose={handleFecharAvaliacao}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Como foi sua primeira experiência com a Docen?
                </DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Sua opinião ajuda a melhorar a geração de atividades, avaliações e planos de aula para você e outros professores. 😊
                    </Typography>

                    {/* Estrelas */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                        <Rating
                            value={avaliacaoEstrelas}
                            onChange={handleChangeEstrelas}
                            size="large"
                        />
                    </Box>

                    {/* Motivos + descrição aparecem somente após escolher as estrelas */}
                    {avaliacaoEstrelas > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {avaliacaoEstrelas <= 2
                                    ? "O que não te agradou? (pode marcar mais de um)"
                                    : "O que você mais gostou? (pode marcar mais de um)"}
                            </Typography>

                            <FormGroup sx={{ mb: 2 }}>
                                {(avaliacaoEstrelas <= 2 ? MOTIVOS_NEGATIVOS : MOTIVOS_POSITIVOS).map((motivo) => (
                                    <FormControlLabel
                                        key={motivo}
                                        control={
                                            <Checkbox
                                                checked={avaliacaoMotivos.includes(motivo)}
                                                onChange={() => handleToggleMotivo(motivo)}
                                            />
                                        }
                                        label={motivo}
                                    />
                                ))}
                            </FormGroup>

                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Quer contar um pouco mais? (opcional)
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                value={avaliacaoDescricao}
                                onChange={(e) => setAvaliacaoDescricao(e.target.value)}
                                placeholder="Conte em poucas palavras como foi sua experiência..."
                            />
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={handleFecharAvaliacao}
                        disabled={avaliacaoLoading}
                    >
                        Pular
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleEnviarAvaliacao}
                        disabled={!avaliacaoEstrelas || avaliacaoLoading}
                    >
                        {avaliacaoLoading ? "Enviando..." : "Enviar avaliação"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para atualizar plano */}
            <UpgradePlanDialog
                open={openUpgrade}
                onClose={() => setOpenUpgrade(false)}
                userId={user?.id}
                planoAtual={planoTipo}
                onUpgraded={async () => {
                    await fetchStatusPlano(user?.id);
                    showSnackbar("Plano atualizado com sucesso!", "success");
                }}

            />

        </>

    );
}